/**
 * @module api/feeds
 *
 * API functions for managing podcasts (feeds): CRUD operations, scheduler
 * control, podcast checking, OPML export, disk space queries, and episode
 * retrieval with search/sort/pagination support.
 */

import { api } from "./config.js";
import { useAppMenu } from "@/composables/useAppMenu.js";

/**
 * Start the server-side scheduler that periodically checks all podcast
 * sources for new episodes (default: every 3 hours in production).
 *
 * @returns {Promise<Object>} Server response data confirming scheduler start
 * @throws {Error} If the server responds with an error status
 */
const startSchedulerOnServer = async () => {
  try {
    // const response = await api.post("/schedule", { data: { cronExpression: } });
    const response = await api.post("/schedule");
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with a status code outside 2xx
      throw new Error(
        error.response.data.message ||
          "Failed to start the scheduler on server",
      );
    } else if (error.request) {
      // Request was sent but no response was received (network error)
      console.log("No response received:", error.request);
    } else {
      // Error occurred while setting up the request
      console.log("Error:", error.message);
    }
  }
};

/**
 * Stop the server-side podcast checking scheduler.
 *
 * @returns {Promise<Object>} Server response data confirming scheduler stop
 * @throws {Error} If the server responds with an error status
 */
const stopSchedulerOnServer = async () => {
  try {
    const response = await api.delete("/schedule");
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with a status code outside 2xx (e.g., 400)
      throw new Error(
        error.response.data.message || "Failed to stop the scheduler on server",
      );
    } else if (error.request) {
      // Request was made, but no response received (e.g., network error)
      console.log("No response received:", error.request);
    } else {
      // Error setting up the request
      console.log("Error:", error.message);
    }
  }
};

/**
 * Refresh the list of destination podcasts used in "Move to Podcast" dropdowns.
 * Fetches all podcasts from the server, filters to only "default" type podcasts
 * (excluding auto-generated ones), and updates the shared destinationPodcasts ref
 * in the useAppMenu composable.
 */
const refreshDestinationPodcasts = async () => {
  const { destinationPodcasts } = useAppMenu();

  try {
    // Fetch all podcasts from the server
    const response = await getFeeds();

    // Defensive: handle cases where API returns unexpected structure
    if (!response || !Array.isArray(response.podcasts)) {
      console.warn("refreshDestinationPodcasts: unexpected API response", response);
      destinationPodcasts.value = [];
      return;
    }

    // Move targets: user "default" podcasts PLUS the system "Archivio"
    // (type "archive") so episodes can be moved into the archive from the
    // dropdown. Keep the episode_count so the move dialog can show it as a
    // subtitle. Map to a simplified shape for the select options.
    destinationPodcasts.value = response.podcasts
      .filter((x) => x.type == "default" || x.type == "archive")
      .map((x) => {
        return { title: x.title, id: x.id, episode_count: x.episode_count };
      });
  } catch (error) {
    console.warn("refreshDestinationPodcasts failed:", error.message);
    destinationPodcasts.value = [];
  }
};

/**
 * Download all podcasts as an OPML file.
 * Creates a temporary blob URL, triggers a download via a dynamically
 * created anchor element, then cleans up the blob URL and element.
 *
 * @throws {Error} If the OPML download request fails
 */
const downloadOpml = async () => {
  try {
    // Request OPML as a binary blob to preserve file integrity
    const response = await api.get("/podcasts/download-opml", {
      responseType: "blob",
    });

    // Create a temporary object URL from the response blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    // Create a hidden anchor element to trigger the browser's download dialog
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "podcasts.opml");
    document.body.appendChild(link);
    link.click();

    // Clean up the temporary URL and DOM element to prevent memory leaks
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to get OPML file");
  }
};

/**
 * Update an existing podcast's metadata on the server.
 *
 * @param {Object} podcastData - The podcast data to update (title, URL, type, etc.)
 * @returns {Promise<Object>} Server response data with updated podcast
 * @throws {Error} If the update fails
 */
const editPodcast = async (podcastData) => {
  try {
    const response = await api.post("/podcasts", { podcastData });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to get feeds");
  }
};

/**
 * Create a new podcast on the server.
 *
 * @param {Object} podcastData - The podcast data (title, source URL, type, etc.)
 * @returns {Promise<Object>} Server response data with created podcast
 * @throws {Error} If the creation fails
 */
const addPodcast = async (podcastData) => {
  try {
    const response = await api.post("/podcasts", { podcastData });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to get feeds");
  }
};

/**
 * Trigger an immediate check of all podcast sources for new episodes.
 * This is the "Check All" admin action that bypasses the scheduler interval.
 * Uses the bulk check lookback period (default: 3 days).
 *
 * @returns {Promise<Object>} Server response data with check results
 * @throws {Error} If the check request fails
 */
const checkPodcasts = async () => {
  try {
    const response = await api.get(`/admin/check-now`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to check all podcasts",
    );
  }
};

/**
 * Trigger an immediate check of a single podcast for new episodes.
 * Uses the single podcast lookback period (default: 7 days), which is
 * longer than the bulk check to catch episodes that may have been missed.
 *
 * @param {number} podcastId - The ID of the podcast to check
 * @returns {Promise<Object>} Server response data with check results
 * @throws {Error} If the check request fails
 */
const checkPodcast = async (podcastId) => {
  try {
    const response = await api.get(`/admin/check-now/${podcastId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to check podcast");
  }
};

/**
 * Get the server's disk space usage information.
 *
 * @returns {Promise<Object>} Server response data with disk space details
 * @throws {Error} If the request fails
 */
// July 27, 2025
const getDiskSpace = async () => {
  try {
    const response = await api.get(`/admin/check-space`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get server disk space",
    );
  }
};

/**
 * Get the consolidated storage report: server disk, proxy disk, and R2 usage
 * split into cache vs saved files. Powers the admin "Storage" dialog.
 *
 * @returns {Promise<Object>} { success, data: { server, proxy, r2 } } (raw bytes)
 * @throws {Error} If the request fails
 */
const getStorageStats = async () => {
  try {
    const response = await api.get(`/admin/storage`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get storage report",
    );
  }
};

/**
 * Fetch all podcasts (feeds) from the server with optional query parameters
 * for search and sorting.
 *
 * @param {Object} [params] - Optional query parameters
 * @param {string} [params.q] - Search query string
 * @param {string} [params.sort_by] - Field to sort by (e.g., "updated_at", "title")
 * @param {string} [params.sort_direction] - Sort direction ("ASC" or "DESC")
 * @returns {Promise<Object>} Server response data containing { podcasts, total }
 * @throws {Error} If the fetch fails
 */
const getFeeds = async (params) => {
  try {
    const response = await api.get("/podcasts", { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to get feeds");
  }
};

/**
 * Fetch a single feed (podcast) by its ID.
 *
 * @param {number} feedId - The feed/podcast ID to retrieve
 * @returns {Promise<Object>} The feed object
 * @throws {Error} If the fetch fails
 */
const getFeed = async (feedId) => {
  try {
    const response = await api.get(`/feeds/${feedId}`);
    return response.data.feed;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to get feed: ${feed}`,
    );
  }
};

/**
 * Delete a podcast by its ID. Due to CASCADE deletes on junction tables
 * (podcast_episodes, etc.), this also removes all podcast-episode associations.
 *
 * @param {number} feedId - The podcast ID to delete
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the deletion fails
 */
const deletePodcast = async (feedId) => {
  try {
    const response = await api.delete(`/podcast/${feedId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to delete feed`);
  }
};

/**
 * Search episodes across all podcasts with pagination support.
 * Used by the "Search All Episodes" feature.
 *
 * @param {string|null} searchTerm - Text to search for in titles/descriptions/tag names
 * @param {string|null} download_status - Filter by download status (e.g., "cached", "downloading")
 * @param {number} page - Current page number for pagination
 * @param {number} pageSize - Number of episodes per page
 * @param {number|null} tag - Tag filter: a tag id the episode must carry
 * @returns {Promise<Object>} Server response with { episodes, total, pagination }
 * @throws {Error} If the search fails
 */
const getAllFeedsEpisodes = async (searchTerm = null,
  download_status = null,
  page, pageSize, tag = null) => {
  const params = { page, pageSize };
  // Only include search/filter params if they have values
  if (searchTerm) params.q = searchTerm;
  if (download_status) params.d = download_status;
  if (tag) params.tag = tag; // tag filter: a tag id the episode must carry
  try {
    const response = await api.get(`/episodes/search`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
};

/**
 * Fetch episodes for a specific podcast with search, sort, and pagination support.
 *
 * @param {number} id - The podcast ID whose episodes to retrieve
 * @param {string|null} searchTerm - Text to search for in episode titles/descriptions
 * @param {string|null} download_status - Filter by download status
 * @param {string} sort_by - Field to sort by (e.g., "published_at", "title")
 * @param {string} sort_direction - Sort direction ("ASC" or "DESC")
 * @param {number} page - Current page number for pagination
 * @param {number} pageSize - Number of episodes per page
 * @param {number} no_pagination - If 1, disables pagination and retrieves all episodes
 * @param {number|null} tag - Tag filter: a tag id the episode must carry
 * @returns {Promise<Object>} Server response with { episodes, total, pagination }
 * @throws {Error} If the fetch fails
 */
const getFeedEpisodes = async (
  id,
  searchTerm = null,
  download_status = null,
  sort_by,
  sort_direction,
  page,
  pageSize,
  no_pagination,
  tag = null,
) => {
  const params = { page, pageSize };
  // Only include search/filter params if they have values
  if (searchTerm) params.q = searchTerm;
  if (download_status) params.d = download_status;
  if (tag) params.tag = tag; // tag filter: a tag id the episode must carry
  params.sort_by = sort_by;
  params.sort_direction = sort_direction;
  params.no_pagination = no_pagination; // if = 1, disable pagination management on server (retrieves all episodes of the podcast)
  try {
    const response = await api.get(`/episodes/podcast/${id}`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
};

/**
 * Exported API object containing all podcast/feed-related functions.
 * Consumed by the barrel export in api/index.js and by composables.
 */
export const podcastsApi = {
  startSchedulerOnServer,
  stopSchedulerOnServer,
  refreshDestinationPodcasts,
  //
  getDiskSpace,
  getStorageStats,
  checkPodcast,
  checkPodcasts,
  addPodcast,
  editPodcast,
  // checkFeeds,
  getFeeds,
  getFeed,
  getFeedEpisodes,
  getAllFeedsEpisodes,
  deletePodcast,
  downloadOpml,
  // getOpml,
};
